import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Loader2, ShieldAlert } from "lucide-react";

const MIN_LEN = 10;
const FIELDS = [
  { key: "current", label: "Current password", testid: "current-password-input", autoComplete: "current-password" },
  { key: "next", label: "New password", testid: "new-password-input", autoComplete: "new-password" },
  { key: "confirm", label: "Confirm new password", testid: "confirm-password-input", autoComplete: "new-password" },
];

export default function ChangePasswordDialog({ open, onOpenChange, forced = false, onChanged }) {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < MIN_LEN) return toast.error(`New password must be at least ${MIN_LEN} characters`);
    if (form.next !== form.confirm) return toast.error("Passwords do not match");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Password updated");
      setForm({ current: "", next: "", confirm: "" });
      await onChanged?.();
      onOpenChange(false);
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={forced ? undefined : onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="change-password-dialog" hideClose={forced}
        onEscapeKeyDown={(e) => forced && e.preventDefault()} onPointerDownOutside={(e) => forced && e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2"><KeyRound className="h-5 w-5 text-teal-600" /> {forced ? "Set a new password" : "Change Password"}</DialogTitle>
          <DialogDescription>Use at least {MIN_LEN} characters with letters and numbers. You stay signed in after changing.</DialogDescription>
        </DialogHeader>
        {forced && (
          <div data-testid="force-password-notice" className="flex gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" /> You're using the default password. Please set a new one before continuing.
          </div>
        )}
        <form onSubmit={submit} className="space-y-4">
          {FIELDS.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <Label>{f.label}</Label>
              <Input data-testid={f.testid} type="password" required value={form[f.key]} onChange={set(f.key)} autoComplete={f.autoComplete} />
            </div>
          ))}
          <Button data-testid="change-password-submit" type="submit" disabled={saving} className="w-full bg-teal-600 hover:bg-teal-700 text-white">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Update Password
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
